// The API has no child/parent relationship — a child journal only carries its
// own `first-name` (see docs/child_journal_editions.md §4). Grouping journals
// into "children" is therefore a heuristic: journals sharing a (trimmed,
// case-insensitive) first name are treated as the same child. This is the same
// assumption the product's own "which child is this book about?" picker makes.
// Two different children who happen to share a first name will be grouped
// together — there is no backend id to disambiguate them.
export const normalizedChildName = name => (name || '').trim().toLowerCase();

// `journals`: [{ id, firstName, createdAt, ...rest }]. Each group's `journals`
// are sorted oldest-first (so index 0 is "Book 1" for that child); groups
// themselves are sorted by their most recently created journal, newest first.
export const groupJournalsByChild = (journals = []) => {
  const groups = new Map();

  journals.forEach(journal => {
    const key = normalizedChildName(journal.subjectId);
    if (!key) return;
    if (!groups.has(key)) groups.set(key, { key, journalId: journal.id, firstName: journal.firstName, journals: [] });
    groups.get(key).journals.push(journal);
  });

  return Array.from(groups.values())
    .map(group => ({
      ...group,
      journals: [...group.journals].sort(
        (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
      ),
    }))
    .sort((a, b) => {
      const latest = list => Math.max(...list.journals.map(j => new Date(j.createdAt || 0).getTime()));
      return latest(b) - latest(a);
    });
};

// A sensible default when a route doesn't name a journal and there's no sticky
// last-viewed one — the most recently created journal, so a member who just
// added a new child or a new year lands there rather than on an arbitrary
// (often much older) one.
export const pickNewestJournalId = (journals = []) => {
  if (!journals.length) return null;
  return [...journals].sort(
    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
  )[0].id;
};
